import styled from '@emotion/styled';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { alphaHex, colors } from '@/constants/colors';
import nextArrowDisabled from '@/public/images/ic-pagination-next-disabled.svg';
import nextArrow from '@/public/images/ic-pagination-next.svg';
import prevArrowDisabled from '@/public/images/ic-pagination-prev-disabled.svg';
import prevArrow from '@/public/images/ic-pagination-prev.svg';

type PaginatedPaginationProps = {
  pageCount: number,
  showPageCount?: number,
  currentPage: number,
  onChange: (newPage: number) => void,
};

const PaginationContainer = styled.div`
display: flex;
flex-direction: row;
justify-content: center;
margin-top: 50px;
gap: 14px;
`;

const PaginationItem = styled.button<{ selected?: boolean }>`
display: flex;
width: 28px;
height: 28px;
align-items: center;
justify-content: center;
border: 0px;
background-color: ${({ selected }) => selected ? colors.indigo : colors.transparent};
border-radius: 2px;
color: ${({ selected }) => selected ? colors.white : colors.indigo};
cursor: pointer;
font-size: 13px;
font-weight: ${({ selected }) => selected ? 700 : 500};
line-height: 23px;
opacity: ${({ selected }) => selected ? '100%' : '80%'};

:disabled {
  cursor: not-allowed;
}
`;

const PaginationArrow = styled(PaginationItem)`
border: 1px solid ${colors.black}${alphaHex(0.09)};
opacity: 100%;
`;

const PaginatedPagination = ({ pageCount, showPageCount = 7, currentPage, onChange }: PaginatedPaginationProps) => {
  const [currentPageRange, setCurrentPageRange] = useState<number[]>([]);

  useEffect(() => {
    if (pageCount <= showPageCount) {
      setCurrentPageRange(Array.from({ length: pageCount }, (_, i) => i + 1));
      return;
    }

    const showHalfRange = Math.floor(showPageCount / 2);
    const min = Math.max(1, currentPage - showHalfRange);

    setCurrentPageRange(Array.from({ length: showPageCount }, (_, i) => {
      if (min + showPageCount - 1 <= pageCount) {
        return min + i;
      }
      return pageCount - showPageCount + 1 + i;
    }));
  }, [currentPage, showPageCount, pageCount]);

  return (
    <PaginationContainer>
      <PaginationArrow
        onClick={() => onChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <Image src={currentPage === 1 ? prevArrowDisabled : prevArrow} alt="prev arrow" />
      </PaginationArrow>
      {currentPageRange.map((pageNumber) =>
        (
          <PaginationItem
            key={pageNumber}
            selected={pageNumber === currentPage}
            onClick={() => onChange(pageNumber)}
          >
            <span>{pageNumber}</span>
          </PaginationItem>
        ))}
      <PaginationArrow
        onClick={() => onChange(currentPage + 1)}
        disabled={currentPage === pageCount}
      >
        <Image src={currentPage === pageCount ? nextArrowDisabled : nextArrow} alt="next arrow" />
      </PaginationArrow>
    </PaginationContainer>
  );
};

export default PaginatedPagination;
